"use client"

/**
 * Bloom's and difficulty on a Leo spotted card.
 *
 * Same chips, reason, and Apply as the Ask Leo classification card.
 * The author reviews the suggestion here instead of opening a chat.
 */

import * as React from "react"

import { Button } from "@/components/ui/button"
import { FilterChipGroup } from "@/components/ui/filter-chip-group"
import {
  AUTHORING_BLOOM_OPTIONS,
  AUTHORING_DIFFICULTY_OPTIONS,
} from "@/lib/library-authoring"
import type { LibraryBloomLevel } from "@/lib/mock/library"
import {
  bloomReasonForStem,
  difficultyReasonForStem,
} from "@/lib/question-quality/classification-ask"
import {
  suggestBloomLevel,
  suggestDifficultyTag,
  type SuggestedDifficulty,
} from "@/lib/question-quality/suggest-tags"

const BLOOM_CHIP_OPTIONS = AUTHORING_BLOOM_OPTIONS.map((row) => ({
  value: row.value,
  label: row.label,
}))

const DIFFICULTY_CHIP_OPTIONS = AUTHORING_DIFFICULTY_OPTIONS.map((row) => ({
  value: row.value,
  label: row.label,
}))

/** Same row as Diagnose, Fix quality, and Skip on the spotted card. */
const LEVEL_ACTION_CLASS =
  "min-w-[8.5rem] flex-[1_1_calc(50%-0.375rem)] justify-center"

function LevelReason({ reason }: { reason: string }) {
  const [open, setOpen] = React.useState(false)
  return (
    <div className="min-w-0">
      <Button
        type="button"
        variant="link"
        size="sm"
        className="h-auto px-0 py-0 text-xs font-normal text-muted-foreground"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {open ? "Hide rationale" : "View rationale"}
      </Button>
      {open ? (
        <p className="m-0 mt-1 text-sm leading-5 text-muted-foreground">{reason}</p>
      ) : null}
    </div>
  )
}

export function LeoSpottedLevelFields({
  stem,
  type = "",
  showBloom,
  showDifficulty,
  disabled,
  onApply,
  onSkip,
}: {
  stem: string
  type?: string
  showBloom: boolean
  showDifficulty: boolean
  disabled?: boolean
  onApply: (next: {
    bloom?: LibraryBloomLevel
    difficulty?: SuggestedDifficulty
  }) => void
  onSkip?: () => void
}) {
  const suggestedBloom = suggestBloomLevel(stem) ?? "Understand"
  const suggestedDifficulty = suggestDifficultyTag(stem, type) ?? "medium"
  const [bloom, setBloom] = React.useState<LibraryBloomLevel>(suggestedBloom)
  const [difficulty, setDifficulty] = React.useState<SuggestedDifficulty>(suggestedDifficulty)

  if (!showBloom && !showDifficulty) return null

  return (
    <div className="flex min-w-0 flex-col gap-3">
      {showBloom ? (
        <section className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">Bloom&apos;s</span>
          <FilterChipGroup
            value={bloom}
            onValueChange={(value) => setBloom(value as LibraryBloomLevel)}
            options={BLOOM_CHIP_OPTIONS}
            variant="brand"
            size="sm"
            aria-label="Bloom's"
          />
          <LevelReason reason={bloomReasonForStem(stem, bloom)} />
        </section>
      ) : null}
      {showDifficulty ? (
        <section className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">Difficulty</span>
          <FilterChipGroup
            value={difficulty}
            onValueChange={(value) => setDifficulty(value as SuggestedDifficulty)}
            options={DIFFICULTY_CHIP_OPTIONS}
            variant="brand"
            size="sm"
            aria-label="Difficulty"
          />
          <LevelReason reason={difficultyReasonForStem(stem, type, difficulty)} />
        </section>
      ) : null}
      <div className="flex min-w-0 flex-wrap gap-1.5 pt-0.5">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={LEVEL_ACTION_CLASS}
          disabled={disabled}
          onClick={() =>
            onApply({
              ...(showBloom ? { bloom } : {}),
              ...(showDifficulty ? { difficulty } : {}),
            })
          }
        >
          Apply
        </Button>
        {onSkip ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={`${LEVEL_ACTION_CLASS} text-muted-foreground`}
            disabled={disabled}
            onClick={onSkip}
          >
            Skip
          </Button>
        ) : null}
      </div>
    </div>
  )
}
